import { BackIcon } from '@euler/app/components/icons/BackIcon';
import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { LinearGradient } from 'expo-linear-gradient';
import { useEffect, useMemo } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import { HeaderRight } from './components/HeaderRight';
import { HeaderTitle } from './components/HeaderTitle';
import { ServiceSubjectsCatalog } from './components/ServiceSubjectsCatalog';
import { ServiceSubjectsHost, ServiceSubjectsHostProvider } from './host';

export const ServiceSubjectsScreen = wrapNavigatorScreen(
  () => {
    const navigation = useNavigation<StackNavigationProp<any>>();
    const { width } = useWindowDimensions();
    const { rpx } = useResponsiveContext();

    useEffect(() => {
      navigation.setOptions({
        headerTitle: () => <HeaderTitle />,
        headerRight: () => <HeaderRight />,
        headerTitleAlign: 'center',
        headerTitleStyle: {
          alignSelf: 'center',
          flex: 1,
        },
        headerTitleContainerStyle: [
          {
            position: 'absolute',
            left: rpx(44),
            right: rpx(44),
            maxWidth: undefined,
          },
          Platform.OS === 'web'
            ? {
                top: '50%',
                transform: [{ translateY: '-50%' as any }],
              }
            : {
                bottom: rpx(6),
              },
        ],
        headerRightContainerStyle: {
          width: rpx(44),
          flexDirection: 'row',
          justifyContent: 'flex-end',
          alignItems: Platform.OS === 'web' ? 'center' : 'flex-end',
          paddingRight: rpx(15),
          paddingBottom: Platform.OS === 'web' ? 0 : rpx(12),
        },
        cardStyle: {
          backgroundColor: '#fff',
        },
        headerBackground: () => (
          <LinearGradient
            start={{ x: 0, y: 0.5 }}
            end={{ x: 1, y: 1 }}
            colors={['#006be4', '#5ba0e0']}
            locations={[0.335, 1]}
            css={`
              flex: 1;
            `}
          />
        ),
        headerBackImage: () => <BackIcon fill="#fff" size={rpx(50)} />,
      });
    }, [navigation, width, rpx]);

    return (
      <OrderServiceSubjectsHost>
        <ServiceSubjectsCatalog />
      </OrderServiceSubjectsHost>
    );
  },
  {
    title: '服务项目',
  },
);

// Adapts the order flow's context to the catalog host, reusing the very same
// subject instances so the navigator header (search / custom-add) and the catalog
// body stay in sync.
const OrderServiceSubjectsHost = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const ctx = useOrderFlowContext();
  const { form } = ctx.formController;
  const licensePlateNo = form.licensePlateNo.value;
  const vin = form.vin.value;
  const host = useMemo<ServiceSubjectsHost>(
    () => ({
      orderDetail$: ctx.orderDetail$,
      updateDetail: ctx.updateDetail,
      inspectionTemplates: ctx.inspectionTemplates,
      showCustomServiceSubjectModal$: ctx.showCustomServiceSubjectModal$,
      serviceSubjectQuery$: ctx.serviceSubjectQuery$,
      skipJobAssignment$: ctx.skipJobAssignment$,
      jobAssignmentEnabled: true,
      capabilities: ctx.capabilities,
      vehicle: { licensePlateNo, vin },
      validateServiceSubjects: () => form.serviceSubjects.validate(),
    }),
    [ctx, form, licensePlateNo, vin],
  );
  return (
    <ServiceSubjectsHostProvider value={host}>
      {children}
    </ServiceSubjectsHostProvider>
  );
};
